'use client';

import { formatPrice, quotedTotal } from '@/lib/price';
import { Microscope } from '@holistic/components/icons';

import type { TalepRequestType } from './types';

type PriceBreakdownProps = {
  items: TalepRequestType[];
  /** Garanti kapsamındaki talepte hiçbir kalemden ücret alınmaz. */
  isWarrantyCovered: boolean;
};

/**
 * Seçilen arızaların kalem kalem tutarı ve toplamı.
 *
 * Gösterilen her tutar ön tekliftir: tahsilat, cihaz incelenip müşteri kesin
 * teklifi onayladıktan sonra yapılır. Bu yüzden başlık ve alt not tutarı asla
 * nihai fiyat gibi sunmaz.
 *
 * Toplam, backend'in `QuotedPrice` hesabıyla aynı kuralda: kalemlerden birinin
 * bile fiyatı yoksa toplam gösterilmez, tutar inceleme sonrasına bırakılır.
 */
const PriceBreakdown = ({ items, isWarrantyCovered }: PriceBreakdownProps) => {
  const total = isWarrantyCovered ? null : quotedTotal(items);
  const needsAnalysis = items.some((i) => i.isAnalysisRequired);

  return (
    <ul className="flex flex-col gap-2.5">
      {items.map((item) => {
        const isQuoted =
          !isWarrantyCovered && item.price !== null && !item.isAnalysisRequired;

        return (
          <li
            key={item.id}
            className="flex items-baseline justify-between gap-4"
          >
            <span className="min-w-0 flex-1 truncate text-[13px] text-gray-600">
              {item.name}
            </span>
            <span className="shrink-0 text-[13px] font-medium text-gray-800">
              {isWarrantyCovered ? (
                'Ücretsiz'
              ) : isQuoted ? (
                formatPrice(item.price!)
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <Microscope className="size-3" />
                  Analiz gerekli
                </span>
              )}
            </span>
          </li>
        );
      })}

      <li className="mt-1 flex items-baseline justify-between gap-4 border-t border-gray-100 pt-3">
        <span className="text-sm font-semibold text-gray-700">
          {isWarrantyCovered ? 'Toplam Tutar' : 'Ön Teklif Toplamı'}{' '}
          <span className="font-normal text-gray-400">(KDV dahil)</span>
        </span>
        <span
          className={
            isWarrantyCovered
              ? 'text-sm font-bold text-emerald-600'
              : 'text-primary text-sm font-bold'
          }
        >
          {isWarrantyCovered
            ? 'Ücretsiz'
            : total !== null
              ? formatPrice(total)
              : 'İnceleme sonrası'}
        </span>
      </li>

      {/* Tutar her hâlükârda ön teklif; analiz isteyen kalem varsa toplam
          hiç oluşmadığı için uyarı ayrıca vurgulanır. */}
      {!isWarrantyCovered && (
        <li className="text-xs leading-relaxed text-amber-700">
          {needsAnalysis
            ? 'Seçtiğiniz arızalardan biri analiz gerektiriyor. Cihazınız incelendikten sonra kesin teklif iletilecek; onaylamadan işleme başlanmaz.'
            : 'Bu tutar bir ön tekliftir. Cihazınız incelendikten sonra kesin teklif iletilir; onayınız olmadan işleme başlanmaz ve tahsilat yapılmaz.'}
        </li>
      )}
    </ul>
  );
};

export default PriceBreakdown;
